import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { messagingService } from '../../services/messagingService';
import GroupChatHeader from './components/GroupChatHeader';
import GroupMessagePanel from './components/GroupMessagePanel';
import TeamRoster from './components/TeamRoster';
import NewGroupChatModal from './components/NewGroupChatModal';
import { Users, MessageSquare, Plus } from 'lucide-react';

const TeamGroupChat = () => {
  const { user, userProfile } = useAuth();
  const [groupConversations, setGroupConversations] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [error, setError] = useState('');
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [showRoster, setShowRoster] = useState(false);
  const subscriptionRef = useRef(null);

  // Check if user can create/moderate group chats
  const canModerateGroups = userProfile?.role === 'city_official' || userProfile?.role === 'pcf_admin';

  useEffect(() => {
    if (user) {
      loadGroupConversations();
    }
  }, [user]);

  // Subscribe to real-time updates
  useEffect(() => {
    if (user) {
      subscriptionRef.current = messagingService?.subscribeToConversations(() => {
        loadGroupConversations();
      });
    }

    return () => {
      if (subscriptionRef?.current) {
        subscriptionRef?.current?.unsubscribe();
      }
    };
  }, [user]);

  const loadGroupConversations = async () => {
    try {
      setLoading(true);
      setError('');
      const allConversations = await messagingService?.getConversations();
      // Filter only group conversations
      const groups = allConversations?.filter(conv => conv?.conversationType === 'group');
      setGroupConversations(groups);
      
      // Auto-select first group if none selected
      if (groups?.length > 0 && !selectedGroup) {
        setSelectedGroup(groups?.[0]);
        loadMessages(groups?.[0]?.id);
      }
    } catch (err) {
      console.error('Error loading group conversations:', err);
      setError('Failed to load group chats. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (conversationId) => {
    try {
      setMessagesLoading(true);
      const data = await messagingService?.getMessages(conversationId);
      setMessages(data);
    } catch (err) {
      console.error('Error loading messages:', err);
      setError('Failed to load messages. Please try again.');
    } finally {
      setMessagesLoading(false);
    }
  };

  const handleGroupSelect = (group) => {
    setSelectedGroup(group);
    loadMessages(group?.id);
  };

  const handleSendMessage = async (content, phoneNumbers = []) => {
    if (!selectedGroup) return;

    try {
      // Send message to group chat
      const newMessage = await messagingService?.sendMessage(selectedGroup?.id, content);
      setMessages(prev => [...prev, newMessage]);

      // If phone numbers provided, send SMS to each
      if (phoneNumbers?.length > 0) {
        for (const phone of phoneNumbers) {
          try {
            await messagingService?.sendMessage(selectedGroup?.id, content, phone);
          } catch (smsError) {
            console.error('SMS failed for', phone, smsError);
          }
        }
      }
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Failed to send message. Please try again.');
    }
  };

  const handleCreateGroup = async (teamId, title) => {
    try {
      const conversationId = await messagingService?.createTeamGroupChat(teamId, title);
      await loadGroupConversations();
      
      // Select the new group
      const newGroup = groupConversations?.find(g => g?.id === conversationId);
      if (newGroup) {
        handleGroupSelect(newGroup);
      }
      
      setShowNewGroup(false);
    } catch (err) {
      console.error('Error creating group:', err);
      setError('Failed to create group chat. Please try again.');
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Users className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Not authenticated</h3>
          <p className="mt-1 text-sm text-gray-500">Please log in to access team chat.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <Users className="h-8 w-8 text-green-600" />
              <h1 className="ml-3 text-2xl font-bold text-gray-900">Team Group Chat</h1>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center text-sm text-gray-500">
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  canModerateGroups ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                }`}>
                  {userProfile?.role || 'User'}
                </span>
                <span className="ml-2">{userProfile?.fullName || user?.email}</span>
              </div>
              
              {canModerateGroups && (
                <button
                  onClick={() => setShowNewGroup(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  New Group
                </button>
              )}
              
              <button
                onClick={() => setShowRoster(!showRoster)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                <Users className="h-4 w-4 mr-2" />
                {showRoster ? 'Hide Roster' : 'Show Roster'}
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex h-full space-x-6">
          {/* Left Sidebar - Group List */}
          <div className="w-1/4 bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Team Groups</h2>
              <p className="text-sm text-gray-500 mt-1">
                {groupConversations?.length} active groups
              </p>
            </div>

            {loading ? (
              <div className="p-4 space-y-4">
                {[...Array(3)]?.map((_, i) => (
                  <div key={i} className="flex items-center space-x-3 p-3 animate-pulse">
                    <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                    <div className="flex-1">
                      <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                      <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : groupConversations?.length === 0 ? (
              <div className="p-4 text-center">
                <MessageSquare className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                <p className="text-sm text-gray-500">No team groups yet.</p>
                {canModerateGroups && (
                  <button
                    onClick={() => setShowNewGroup(true)}
                    className="mt-2 text-sm text-green-600 hover:text-green-700"
                  >
                    Create the first group
                  </button>
                )}
              </div>
            ) : (
              <div className="overflow-y-auto">
                {groupConversations?.map((group) => (
                  <div
                    key={group?.id}
                    onClick={() => handleGroupSelect(group)}
                    className={`flex items-center space-x-3 p-4 cursor-pointer hover:bg-gray-50 border-b border-gray-100 transition-colors ${
                      selectedGroup?.id === group?.id 
                        ? 'bg-green-50 border-green-200' :''
                    }`}
                  >
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <Users className="w-5 h-5 text-green-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-medium text-gray-900 truncate">
                        {group?.title || 'Team Chat'}
                      </h3>
                      <p className="text-xs text-gray-500">
                        {group?.participants?.length || 0} members
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Center - Chat Panel */}
          <div className={`${showRoster ? 'flex-1' : 'w-2/3'} bg-white rounded-lg shadow-sm border border-gray-200`}>
            {selectedGroup ? (
              <>
                <GroupChatHeader
                  group={selectedGroup}
                  canModerate={canModerateGroups}
                  onShowRoster={() => setShowRoster(!showRoster)}
                />
                <GroupMessagePanel
                  group={selectedGroup}
                  messages={messages}
                  onSendMessage={handleSendMessage}
                  loading={messagesLoading}
                  currentUser={user}
                  userProfile={userProfile}
                  canModerate={canModerateGroups}
                />
              </>
            ) : (
              <div className="h-full flex items-center justify-center">
                <div className="text-center">
                  <Users className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Select a team group</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Choose a group from the sidebar to start chatting.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Right Sidebar - Team Roster */}
          {showRoster && (
            <div className="w-1/4 bg-white rounded-lg shadow-sm border border-gray-200">
              <TeamRoster
                group={selectedGroup}
                canModerate={canModerateGroups}
                onClose={() => setShowRoster(false)}
              />
            </div>
          )}
        </div>
      </div>
      {/* Error Display */}
      {error && (
        <div className="fixed bottom-4 right-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded max-w-sm">
          <p className="text-sm">{error}</p>
          <button
            onClick={() => setError('')}
            className="text-red-800 hover:text-red-900 text-sm font-medium ml-2"
          >
            Dismiss
          </button>
        </div>
      )}
      {/* New Group Modal */}
      {showNewGroup && (
        <NewGroupChatModal
          onClose={() => setShowNewGroup(false)}
          onCreateGroup={handleCreateGroup}
        />
      )}
    </div>
  );
};

export default TeamGroupChat;